/* ace-v2-ems.js: the EMS builder (Brief 16, round 2a), the approved prototype's three steps on page.ems.
   Plain, deferred, loaded by sections/ace-v2-ems.liquid after ace-v2.js. Everything it shows comes from the section's
   config (data-v2-ems-config): the words are section settings, the rules are the section's blocks, the products and
   their prices come from the store (Liquid), and the plans only from real selling plans.
   Prices are cents, formatted by window.ace.v2.money / perServe (the ace-money / ace-per-serve mirrors).
   window.ace.ems holds the pure maths and the cart adapter; tests/v2-ems.test.mjs runs them without a DOM. */
(function () {
  'use strict';
  var PHASES = ['before', 'during', 'after'];

  /* ---------------------------------------------------------------- The maths (pure) */
  // A rule's product is a store handle or the name of a coming-soon item; both are matched as handles.
  function key(s) { return String(s == null ? '' : s).toLowerCase().trim().replace(/[^a-z0-9+]+/g, '-').replace(/\+/g, '').replace(/^-+|-+$/g, ''); }
  function findProduct(cfg, ref) { return cfg.products[ref] || cfg.products[key(ref)] || null; }
  function buyable(p) { return !!p && !p.soon && p.pack > 0 && !!p.variant; }
  // Sold out (the variant's own availability) stays in the box and its price, but is never added. Only an explicit false.
  function inStock(p) { return p.available !== false; }

  // Brief 20: a rule applies to its session type inside its length band [min, max) and from its weekly minimum. An empty
  // bound is no bound, so a rule without the fields (an older config) applies to every length.
  // A rule's type is one session-type key or a comma-separated list of them (Shopify allows 50 blocks a section).
  function bound(v) { return v === '' || v == null || isNaN(+v) ? null : +v; }
  function typesOf(r) { return String(r.type == null ? '' : r.type).split(',').map(function (t) { return t.trim(); }); }
  function applies(r, st) {
    var lo = bound(r.min), hi = bound(r.max), wk = bound(r.minWeek);
    return typesOf(r).indexOf(st.type) >= 0 && (lo === null || st.length >= lo) && (hi === null || st.length < hi) && (wk === null || st.perWeek >= wk);
  }
  // During is a rate per hour, rounded up per session (at least one), unless the rule is once a session ('session');
  // before and after are a rate per session.
  function perSession(phase, rate, length, mode) {
    return phase === 'during' && mode !== 'session' ? Math.max(1, Math.ceil(rate * length)) : rate;
  }
  // Brief 20 guards on one session's during serves (items: [{ handle, per, rate }] in rule order): each product at most
  // caps.perHour an hour, after rounding and at least one; all of them at most caps.fluidMl of fluid an hour at
  // caps.serveMl a serve. Over the fluid cap the lowest rate gives way first, the later one on a tie. A trimmed item says so.
  function capDuring(items, length, caps) {
    var out = items.map(function (it) { return { handle: it.handle, per: it.per, rate: it.rate }; });
    if (!caps) return out;
    var each = caps.perHour > 0 ? Math.max(1, Math.floor(caps.perHour * length + 1e-9)) : Infinity;
    var all = caps.fluidMl > 0 && caps.serveMl > 0 ? Math.max(1, Math.floor(caps.fluidMl / caps.serveMl * length + 1e-9)) : Infinity;
    out.forEach(function (o) { if (o.per > each) { o.per = each; o.capped = true; } });
    var sum = function () { return out.reduce(function (a, o) { return a + o.per; }, 0); };
    while (sum() > all) {
      var k = -1;
      out.forEach(function (o, i) { if (o.per > 0 && (k < 0 || o.rate <= out[k].rate)) k = i; });
      out[k].per--;
      out[k].capped = true;
    }
    return out.filter(function (o) { return o.per > 0; });
  }
  // Euan, 27 Oct: a fixed 4-weekly delivery, the box sized to 4 weeks of use in whole packs.
  function packsFor(weekly, pack, weeks) {
    return Math.max(1, Math.ceil(weekly * weeks / pack));
  }
  // A line against the box: "over" when edited above what the weeks need, "short" when it runs out before the next box.
  function supply(l, pack, weeks) {
    if (!l.weekly) return { state: 'ok', spare: 0 };
    var serves = l.qty * pack, need = l.weekly * weeks, rec = packsFor(l.weekly, pack, weeks);
    if (serves < need) return { state: 'short', weeks: Math.max(1, Math.ceil(weeks - serves / l.weekly)), add: l.qty + 1 };
    if (l.qty > rec) return { state: 'over', weeks: Math.floor(serves / l.weekly), back: rec, spare: serves - need };
    return { state: 'ok', spare: serves - need };
  }

  // The recommendation: priced products become box lines, sized to the weeks; coming soon is listed apart.
  // st = { type, length, perWeek, timing: [phases], add: handle or null }. A product used in more than one phase is one
  // line with its serves added up, listed under During when it's used during.
  function recommend(cfg, st) {
    var lines = [], soon = [];
    var pushSoon = function (k, name, phase) { if (!soon.some(function (x) { return x.key === k; })) soon.push({ key: k, name: name, phase: phase }); };
    var addLine = function (handle, ph, per, capped) {
      var have = lines.filter(function (l) { return l.handle === handle; })[0];
      if (have) {
        have.per += per; have.weekly += per * st.perWeek;
        if (ph === 'during') have.phase = 'during';
        if (capped) have.capped = true;
        return;
      }
      var l = { handle: handle, phase: ph, per: per, weekly: per * st.perWeek, qty: 1, rec: true };
      if (capped) l.capped = true;
      lines.push(l);
    };
    PHASES.forEach(function (ph) {
      if (st.timing.indexOf(ph) < 0) return;
      var during = [];
      cfg.rules.forEach(function (r) {
        if (r.phase !== ph || !applies(r, st)) return;
        var per = perSession(ph, r.rate, st.length, r.mode), p = findProduct(cfg, r.product), item = !p && cfg.soonItems[key(r.product)];
        if (buyable(p)) {
          if (ph !== 'during') { addLine(p.handle, ph, per); return; }
          var d = during.filter(function (x) { return x.handle === p.handle; })[0];
          if (d) { d.per += per; d.rate += +r.rate; } else during.push({ handle: p.handle, per: per, rate: +r.rate });
        } else if (p && p.soon) pushSoon(p.handle, p.name, ph);
        else if (item) pushSoon(item.key, item.name, ph);
      });
      capDuring(during, st.length, cfg.caps).forEach(function (d) { addLine(d.handle, 'during', d.per, d.capped); });
    });
    if (st.add) {
      var a = findProduct(cfg, st.add), ai = !a && cfg.soonItems[key(st.add)];
      if (buyable(a)) {
        if (!lines.some(function (l) { return l.handle === a.handle; })) lines.push({ handle: a.handle, phase: a.phase || 'during', per: 1, weekly: st.perWeek, qty: 1, rec: false });
      } else if (a && a.soon) pushSoon(a.handle, a.name, a.phase);
      else if (ai) pushSoon(ai.key, ai.name, ai.phase);
    }
    lines.forEach(function (l) { l.qty = packsFor(l.weekly, cfg.products[l.handle].pack, cfg.weeks); });
    return { lines: lines, soon: soon };
  }

  // The capped lines one phase would put in the box (with coming soon apart). The step 2 cards are built from it, so a
  // card never names a product the box would drop under a guard.
  function phaseLines(cfg, st, phase) {
    var r = recommend(cfg, { type: st.type, length: st.length, perWeek: st.perWeek, timing: [phase], add: null });
    return { lines: r.lines.map(function (l) { return { handle: l.handle, per: l.per }; }), soon: r.soon };
  }

  // Totals in cents. Coming soon never counts; addable is what can be bought now (sold-out lines left out). The subscription total and its saving come only from each line's
  // real selling plan; with any line lacking one, both are null (no number is shown).
  function totals(lines, products) {
    var once = 0, addable = 0, sub = 0, subAddable = 0, pct, samePct = true, every = true, n = 0;
    lines.forEach(function (l) {
      var p = products[l.handle];
      if (!buyable(p) || !(l.qty > 0)) return;
      n++;
      once += p.price * l.qty;
      if (inStock(p)) addable += p.price * l.qty;
      var plan = p.plans && p.plans[0];
      if (!plan) { every = false; return; }
      sub += plan.price * l.qty;
      if (inStock(p)) subAddable += plan.price * l.qty;
      if (pct === undefined) pct = plan.pct;
      else if (pct !== plan.pct) samePct = false;
    });
    if (!n || !every) return { once: once, addable: addable, sub: null, saving: null, pct: null, subAddable: null, savingAddable: null };
    return { once: once, addable: addable, sub: sub, saving: once - sub, pct: samePct && pct != null ? pct : null, subAddable: subAddable, savingAddable: addable - subAddable };
  }
  function canSubscribe(lines, products, bbId) {
    return !!bbId && totals(lines, products).sub !== null;
  }

  // The cart adapter: one function builds the lines Appstle's Build-a-Box needs (the contract and its evidence are in
  // docs/APPSTLE.md): { id, quantity, selling_plan, properties: { _appstle-bb-id (the box's uniqueRef),
  // _appstle_bundles_type, __appstle-bb-name, _appstle-bb-product-sku, __appstle-bb-variants } }; a one-off is the same lines
  // without selling_plan. Before Appstle there is no box id, so a one-off is plain { id, quantity } lines.
  function cartLines(lines, products, opts) {
    opts = opts || {};
    var out = [];
    lines.forEach(function (l) {
      var p = products[l.handle];
      if (!buyable(p) || !inStock(p) || !(l.qty > 0)) return;
      var item = { id: p.variant, quantity: l.qty };
      if (opts.mode === 'sub') {
        var plan = p.plans && p.plans[0];
        if (!plan) throw new Error('ace-v2-ems: no selling plan for ' + p.handle);
        item.selling_plan = plan.id;
      }
      if (opts.bbId) {
        item.properties = { '_appstle-bb-id': opts.bbId, _appstle_bundles_type: 'CLASSIC_BUILD_A_BOX' };
        if (opts.bbName) item.properties['__appstle-bb-name'] = opts.bbName;
        item.properties['_appstle-bb-product-sku'] = p.sku || '';
        item.properties['__appstle-bb-variants'] = p.variant + ':' + l.qty;
      }
      out.push(item);
    });
    return out;
  }

  function soldOut(lines, products) {
    return lines.filter(function (l) { var p = products[l.handle]; return buyable(p) && !inStock(p); }).map(function (l) { return l.handle; });
  }

  window.ace = window.ace || {};
  window.ace.ems = { soldOut: soldOut, key: key, typesOf: typesOf, applies: applies, phaseLines: phaseLines, capDuring: capDuring, perSession: perSession, packsFor: packsFor, supply: supply, recommend: recommend, totals: totals, canSubscribe: canSubscribe, cartLines: cartLines };
  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- The builder (DOM) */
  var d = document;
  var $ = function (s, r) { return (r || d).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || d).querySelectorAll(s)); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
  // Fill a setting's [tokens]. Values are HTML the builder made (figures, prices) or escaped text.
  var fill = function (tpl, v) { return esc(tpl).replace(/\[(\w+)\]/g, function (m, k) { return k in v ? v[k] : m; }); };
  var fig = function (n, unit) { return '<span class="v2-fig">' + esc(n) + '</span>' + (unit ? '&nbsp;<span class="v2-unit">' + esc(unit) + '</span>' : ''); };

  function init(root) {
    var cfg = JSON.parse($('[data-v2-ems-config]', root).textContent);
    var T = cfg.t, E = window.ace.ems, V = window.ace.v2 || {};
    var price = function (c) { return '<span class="ace-price">' + esc(V.money ? V.money(c) : c) + '</span>'; };
    cfg.products = cfg.products || {};
    cfg.soonItems = cfg.soonItems || {};
    cfg.rules.forEach(function (r) { r.rate = parseFloat(r.rate) || 0; });
    // Brief 20: the length runs from len.min to len.max in len.step hours; each sport's suggestion is snapped onto it.
    var L = { min: parseFloat(cfg.len && cfg.len.min) || 0.5, max: parseFloat(cfg.len && cfg.len.max) || 6, step: parseFloat(cfg.len && cfg.len.step) || 0.5 };
    var snap = function (h) { return Math.min(L.max, Math.max(L.min, Math.round((h - L.min) / L.step) * L.step + L.min)); };
    cfg.sports.forEach(function (s) { s.length = snap(parseFloat(s.length) || 1); s.perWeek = parseInt(s.perWeek, 10) || 1; });
    Object.keys(cfg.products).forEach(function (h) { var p = cfg.products[h]; p.pack = parseInt(p.pack, 10) || 0; p.carb = parseFloat(p.carb) || null; });
    var W = cfg.weeks;
    var sports = {}, types = {};
    cfg.sports.forEach(function (s) { sports[s.key] = s; });
    cfg.types.forEach(function (t) { types[t.key] = t; });
    var P = cfg.products, bbId = root.getAttribute('data-bb') || '';
    var priced = Object.keys(P).map(function (h) { return P[h]; }).filter(function (p) { return !p.soon && p.pack > 0 && p.variant; });
    var anyPlans = priced.some(function (p) { return p.plans && p.plans.length; });
    root.setAttribute('data-plans', String(anyPlans && !!bbId));

    var S = { step: 1, sport: null, type: null, length: null, perWeek: null, timing: ['during'], lines: [], soon: [], edited: false, notify: {} };
    var qs = new URLSearchParams(location.search), addParam = qs.get('add');
    var el = function (k) { return $('[data-v2-e-' + k + ']', root); };
    var phaseName = function (ph) { return cfg.phases[ph] || ''; };
    var fmtLen = function (h) { return fill(T.len_format, { n: fig(h) }); };
    var stNow = function (type) { return { type: type || S.type, length: S.length, perWeek: S.perWeek }; };
    var timingList = function () { return PHASES.filter(function (t) { return S.timing.indexOf(t) >= 0; }).map(phaseName).join(' + '); };
    var nameOf = function (k) { return (P[k] && P[k].name) || (cfg.soonItems[k] && cfg.soonItems[k].name) || k; };
    var plural = function (n, one, many) { return fill(n === 1 ? one : many, { n: fig(n) }); };
    var subOk = function () { return E.canSubscribe(S.lines, P, bbId); };

    function freshBox() {
      var r = E.recommend(cfg, { type: S.type, length: S.length, perWeek: S.perWeek, timing: S.timing, add: addParam });
      S.lines = r.lines; S.soon = r.soon; S.edited = false;
    }
    function supplyLine() {
      var short = S.lines.filter(function (l) { return E.supply(l, P[l.handle].pack, W).state === 'short'; });
      if (short.length) return fill(short.length > 1 ? T.supply_short_many : T.supply_short_one, { product: short.map(function (l) { return esc(P[l.handle].name); }).join(' ' + esc(T.and_word) + ' ') });
      var spare = S.lines.reduce(function (a, l) { var x = E.supply(l, P[l.handle].pack, W); return a + (x.spare || 0); }, 0);
      if (!S.lines.some(function (l) { return l.weekly; })) return '';
      return spare ? fill(T.supply_spare, { spare: plural(spare, T.spare_one, T.spare_many), weeks: fig(W) }) : fill(T.supply_exact, { weeks: fig(W) });
    }

    /* Step 2: session type, length, frequency and timing on one screen, prefilled from the sport. */
    function renderSessions() {
      var sp = sports[S.sport], sport = esc(sp.label.toLowerCase());
      el('s2-text').innerHTML = fill(T.s2_text, { sport: sport });
      var order = cfg.types.map(function (t) { return t.key; }).sort(function (a, b) { return (b === sp.type) - (a === sp.type); });
      el('types').innerHTML = order.map(function (k) {
        var t = types[k];
        // The products this type puts in the box during a session of the length chosen, guards included.
        var during = E.phaseLines(cfg, stNow(k), 'during').lines.map(function (l) { return esc(nameOf(l.handle)); }).join(' + ');
        return '<label class="v2-e-opt v2-e-type"><input type="radio" name="type" value="' + esc(k) + '"' + (S.type === k ? ' checked' : '') + '>' +
          '<span class="v2-e-sug">' + (k === sp.type ? fill(T.suggested_for, { sport: sport }) : '') + '</span><span class="v2-e-tick"><i class="v2-i v2-i-check"></i></span>' +
          '<span class="v2-name">' + esc(t.label) + '</span><span class="v2-e-ds">' + esc(t.desc) + '</span>' +
          (during ? '<span class="v2-e-rows"><span><b>' + esc(phaseName('during')) + '</b><span>' + during + '</span></span></span>' : '') + '</label>';
      }).join('');
      renderLen();
      var weeks = '';
      for (var n = 1; n <= cfg.maxWeek; n++) {
        weeks += '<label><input type="radio" name="week" value="' + n + '"' + (S.perWeek === n ? ' checked' : '') + ' aria-label="' + esc(T.week_aria.replace('[n]', n)) + '"><span class="v2-fig">' + n + '</span>' + (sp.perWeek === n ? '<span class="v2-e-s">' + esc(T.suggested) + '</span>' : '') + '</label>';
      }
      el('weeks').innerHTML = weeks;
      renderTimings();
    }
    // The length spinbutton (static markup): its value, the suggestion mark and the buttons at the ends.
    function renderLen() {
      var sb = el('len'), sp = sports[S.sport];
      sb.setAttribute('aria-valuemin', L.min); sb.setAttribute('aria-valuemax', L.max);
      sb.setAttribute('aria-valuenow', S.length); sb.setAttribute('aria-valuetext', String(T.len_format).replace('[n]', S.length));
      el('len-out').innerHTML = fmtLen(S.length);
      el('len-sug').hidden = !(sp && sp.length === S.length);
      $$('[data-len]', root).forEach(function (b) { b.disabled = +b.getAttribute('data-len') < 0 ? S.length <= L.min : S.length >= L.max; });
    }
    function setLen(h) {
      var v = snap(h);
      if (v === S.length) return;
      S.length = v; S.edited = false;
      renderSessions(); updateNext(); renderSummary();
    }
    function renderTimings() {
      el('timings').innerHTML = PHASES.map(function (k) {
        // The box's own capped lines for this timing, then coming soon (Brief 20 fix: as the box, never the bare rules).
        var pl = E.phaseLines(cfg, stNow(), k);
        var items = pl.lines.map(function (l) { return '<span><b>' + esc(nameOf(l.handle)) + '</b><span>' + fill(T.per_session, { n: fig(l.per) }) + '</span></span>'; }).join('') +
          pl.soon.map(function (x) { return '<span><b>' + esc(x.name) + '</b><span>' + esc(cfg.comingSoon) + '</span></span>'; }).join('');
        return '<label class="v2-e-opt v2-e-time"><input type="checkbox" name="timing" value="' + k + '"' + (S.timing.indexOf(k) >= 0 ? ' checked' : '') + '><span class="v2-e-tick"><i class="v2-i v2-i-check"></i></span><span class="v2-name">' + esc(phaseName(k)) + '</span>' + (items ? '<span class="v2-e-rows">' + items + '</span>' : '') + '</label>';
      }).join('');
    }

    /* The rail beside steps 1 and 2: the choices so far and the box they make. */
    function renderSummary() {
      var row = function (k, v) { return '<div><dt class="v2-label">' + esc(k) + '</dt><dd class="' + (v ? '' : 'is-empty') + '">' + (v || esc(T.not_chosen)) + '</dd></div>'; };
      var prev = '';
      if (S.sport) {
        if (!S.edited) freshBox();
        var t = E.totals(S.lines, P), sub = subOk();
        prev = '<div class="v2-e-prev"><span class="v2-label">' + esc(T.box_so_far) + '</span><ul>' +
          (S.lines.map(function (l) { return '<li><span>' + esc(P[l.handle].name) + '</span><span>' + fig(l.qty) + ' × ' + fig(P[l.handle].pack) + '</span></li>'; }).join('') || '<li><span class="v2-muted">' + esc(T.add_during) + '</span></li>') +
          '</ul><div class="v2-e-tot"><span class="v2-note">' + (sub ? fill(T.sum_every, { n: fig(W) }) : esc(T.sum_once)) + '</span><b>' + price(sub ? t.sub : t.once) + '</b></div></div>';
      }
      var ty = S.type && types[S.type];
      el('summary').innerHTML = '<h2 id="v2-e-sum-h">' + esc(T.sum_heading) + '</h2><dl>' +
        row(T.sum_sport, S.sport && esc(sports[S.sport].label)) + row(T.sum_sessions, ty && esc(ty.label)) +
        row(T.sum_length, S.length && fill(T.length_x, { length: fmtLen(S.length), n: fig(S.perWeek) })) + row(T.sum_timing, S.sport && esc(timingList())) + '</dl>' + prev;
    }

    /* Step 3: the box. */
    function renderBox() {
      var sp = sports[S.sport];
      el('strip').innerHTML = [esc(sp.label), esc(types[S.type].label), fill(T.length_x, { length: fmtLen(S.length), n: fig(S.perWeek) }), esc(timingList())]
        .map(function (x) { return '<span>' + x + '</span>'; }).join('<span class="v2-e-dotsep" aria-hidden="true">·</span>') +
        '<button type="button" data-go="2" aria-label="' + esc(T.edit_aria) + '">' + esc(T.edit) + '</button>';

      var html = PHASES.map(function (ph) {
        var ls = S.lines.filter(function (l) { return l.phase === ph; });
        if (!ls.length) return '';
        return '<div class="v2-e-phase-h"><span class="v2-label">' + esc(phaseName(ph)) + '</span><span class="v2-label">' + plural(ls.length, T.items_one, T.items_many) + '</span></div>' + ls.map(lineHTML).join('');
      }).join('');
      el('lines').innerHTML = html || '<p class="v2-e-empty">' + fill(T.empty_timing, { timing: esc(timingList().toLowerCase()) }) + '</p>';

      el('reset').hidden = !S.edited;
      var left = priced.filter(function (x) { return !S.lines.some(function (y) { return y.handle === x.handle; }); });
      var add = el('add');
      add.innerHTML = '<option value="">' + esc(T.add_product) + '</option>' + left.map(function (x) { return '<option value="' + esc(x.handle) + '">' + esc(x.name) + '</option>'; }).join('');
      add.disabled = !left.length;
      el('add-note').textContent = left.length ? '' : T.all_in;

      // Coming soon: for the timings chosen, never in the box or the total. Notify me uses the footer sign-up.
      el('soon').innerHTML = S.soon.length ? '<div class="v2-e-phase-h"><span class="v2-label">' + esc(cfg.comingSoon) + '</span><span class="v2-label">' + esc(T.soon_not_in) + '</span></div>' +
        S.soon.map(function (s) {
          return '<div class="v2-e-soonrow"><div><span class="v2-name">' + esc(s.name) + '</span><span class="v2-note">' + fill(T.soon_note, { phase: esc(phaseName(s.phase)) }) + '</span></div>' +
            '<button type="button" class="v2-btn v2-btn-outline v2-btn-sm" data-v2-notify="' + esc(s.key) + '" data-name="' + esc(s.name) + '" aria-pressed="' + !!S.notify[s.key] + '">' + esc(S.notify[s.key] ? T.notified : cfg.notify) + '</button></div>';
        }).join('') : '';
      renderLogic(); renderCheckout(); renderSummary();
    }
    function lineHTML(l) {
      var p = P[l.handle], i = S.lines.indexOf(l), name = esc(p.name);
      var alts = priced.filter(function (x) { return x.handle !== l.handle && x.phase === l.phase && !S.lines.some(function (y) { return y.handle === x.handle; }); });
      var why = (p.carb ? fill(T.why, { carb: fig(p.carb, cfg.carbUnit), phase: esc(phaseName(l.phase).toLowerCase()) }) + ' ' : '') +
        (l.rec ? fill(T.why_rec, { per: fig(l.per), n: fig(S.perWeek), weekly: fig(l.weekly) }) : fill(T.why_added, { weekly: fig(l.weekly) }));
      var x = E.supply(l, p.pack, W), fix = '';
      if (x.state === 'over') fix = '<p class="v2-e-supply">' + fill(T.over, { n: fig(x.weeks) }) + '</p><div class="v2-e-fixes"><button type="button" class="v2-e-tlink" data-setq="' + i + ':' + x.back + '">' + plural(x.back, T.back_to_one, T.back_to_many) + '</button></div>';
      else if (x.state === 'short') fix = '<p class="v2-e-supply">' + plural(x.weeks, T.short_one, T.short_many) + '</p><div class="v2-e-fixes"><button type="button" class="v2-e-tlink" data-setq="' + i + ':' + x.add + '">' + esc(T.add_pack) + '</button></div>';
      else if (l.weekly) fix = '<p class="v2-e-cover">' + fill(T.cover, { weeks: fig(W), spare: plural(x.spare, T.spare_one, T.spare_many) }) + '</p>';
      var chips = [p.form && cfg.forms[p.form], p.output && cfg.outputs[p.output]].filter(Boolean).map(function (c) { return '<span class="v2-chip">' + esc(c) + '</span>'; }).join('');
      var vals = { product: name, pack: fig(p.pack) };
      if (p.available === false) chips += '<span class="v2-e-soldout">' + esc(T.sold_out) + '</span>';
      return '<div class="v2-e-line" data-i="' + i + '">' +
        '<div class="v2-e-th">' + (p.img || '') + '</div>' +
        '<div><div class="v2-e-nm"><span class="v2-name">' + name + '</span>' + chips + '</div><p class="v2-e-why">' + why + '</p>' +
          (l.phase === 'during' && l.handle === cfg.mixProduct && T.mix_note ? '<p class="v2-e-mix">' + esc(T.mix_note) + '</p>' : '') + fix +
          '<div class="v2-e-acts">' + (alts.length ? '<label class="v2-sr" for="v2-e-swap-' + i + '">' + fill(T.swap_aria, vals) + '</label><select id="v2-e-swap-' + i + '" data-swap="' + i + '"><option value="">' + esc(T.swap) + '</option>' + alts.map(function (a) { return '<option value="' + esc(a.handle) + '">' + esc(a.name) + '</option>'; }).join('') + '</select>' : '') +
          '<button type="button" class="v2-e-tlink" data-rm="' + i + '">' + esc(T.remove) + '</button><a class="v2-e-tlink" href="' + esc(p.url) + '">' + esc(T.details) + '</a></div></div>' +
        '<div class="v2-e-qtyc"><div class="v2-e-qty" role="group" aria-label="' + esc(T.qty_aria.replace('[product]', p.name).replace('[pack]', p.pack)) + '">' +
          '<button type="button" data-q="' + i + '" data-d="-1" aria-label="' + esc(T.fewer.replace('[product]', p.name)) + '"' + (l.qty <= 1 ? ' disabled' : '') + '><i class="v2-i v2-i-minus"></i></button>' +
          '<output aria-live="polite">' + l.qty + '</output>' +
          '<button type="button" data-q="' + i + '" data-d="1" aria-label="' + esc(T.more.replace('[product]', p.name)) + '"><i class="v2-i v2-i-plus"></i></button></div>' +
          '<p class="v2-e-qtynote">' + fill(l.qty === 1 ? T.pack_one : T.pack_many, vals) + '</p></div>' +
        '<div class="v2-e-lp">' + price(p.price * l.qty) + '<span class="v2-note">' + fill(T.a_pack, { price: price(p.price) }) + '</span></div>' +
      '</div>';
    }
    function renderLogic() {
      var rows = [];
      PHASES.forEach(function (ph) { cfg.rules.forEach(function (r) { if (r.phase === ph) rows.push(r); }); });
      // Brief 20: each rule's length band, and whether a guard trimmed it in this box.
      var band = function (r) {
        var lo = r.min == null || r.min === '' ? null : +r.min, hi = r.max == null || r.max === '' ? null : +r.max;
        if (lo === null && hi === null) return esc(T.band_any);
        if (lo !== null && hi !== null) return fill(T.band_range, { min: esc(lo), max: esc(hi) });
        return lo !== null ? fill(T.band_from, { min: esc(lo) }) : fill(T.band_under, { max: esc(hi) });
      };
      var typeNames = function (r) { return E.typesOf(r).map(function (k) { return types[k] ? types[k].label : k; }).join(', '); };
      var th = [T.logic_h_timing, T.logic_h_type, T.logic_h_product, T.logic_h_rule, T.logic_h_band, T.logic_h_week, T.logic_h_status].map(function (h) { return '<th>' + esc(h) + '</th>'; }).join('');
      el('logic').innerHTML = '<table><thead><tr>' + th + '</tr></thead><tbody>' + rows.map(function (r) {
        var on = E.applies(r, stNow()) && S.timing.indexOf(r.phase) >= 0, per = E.perSession(r.phase, r.rate, S.length, r.mode);
        var p = findProduct(cfg, r.product), known = p || cfg.soonItems[E.key(r.product)];
        var line = p && S.lines.filter(function (l) { return l.handle === p.handle; })[0];
        var status = !buyable(p) ? cfg.comingSoon : on && r.phase === 'during' && line && line.capped ? T.capped : T.in_box;
        return '<tr class="' + (on ? 'is-cur' : '') + '"><td>' + esc(phaseName(r.phase)) + '</td><td>' + esc(typeNames(r)) + '</td><td>' + esc(known ? known.name : r.product) + '</td>' +
          '<td>' + fill(r.phase === 'during' && r.mode !== 'session' ? T.rule_hour : T.rule_session, { rate: esc(r.rate) }) + '</td><td>' + band(r) + '</td><td>' + (on ? fig(per) + ' × ' + fig(S.perWeek) + ' = ' + fig(per * S.perWeek) : '') + '</td>' +
          '<td>' + esc(status) + '</td></tr>';
      }).join('') + '</tbody></table>' +
        (cfg.caps && T.logic_caps ? '<p>' + fill(T.logic_caps, { n: fig(cfg.caps.perHour), l: fig(cfg.caps.fluidMl / 1000), ml: fig(cfg.caps.serveMl) }) + '</p>' : '') +
        '<p>' + fill(T.logic_sizing, { weeks: fig(W) }) + '</p>' + (subOk() && T.logic_delivery ? '<p>' + fill(T.logic_delivery, { weeks: fig(W) }) + '</p>' : '') +
        '<p>' + fill(T.logic_presets, { list: cfg.sports.map(function (s) { return fill(T.preset_item, { sport: esc(s.label), length: fmtLen(s.length), n: fig(s.perWeek), type: esc(types[s.type] ? types[s.type].label.toLowerCase() : s.type) }); }).join('; ') }) + '</p>';
    }
    function renderCheckout() {
      var t = E.totals(S.lines, P), has = t.once > 0, sub = has && subOk();
      // Sold out (live stock): those lines stay in the box, but Subscribe and Buy once add only what can be bought, and a
      // partly sold-out box is priced on those lines. With nothing in stock the box keeps its own price, buttons off.
      var out = E.soldOut(S.lines, P), canAdd = t.addable > 0, part = out.length > 0 && canAdd;
      var once = part ? t.addable : t.once, subT = part ? t.subAddable : t.sub, saving = part ? t.savingAddable : t.saving;
      var outNote = part ? '<p class="v2-note v2-e-outnote">' + fill(T.sold_out_note, { product: out.map(function (h) { return esc(P[h].name); }).join(' ' + esc(T.and_word) + ' ') }) + '</p>' : '';
      var buyBtn = function (kind, cls, label, cents) {
        if (has && !canAdd) return '<button type="button" class="v2-btn ' + cls + '" data-buy="' + kind + '" disabled><span>' + esc(T.sold_out) + '</span></button>';
        // The price side is one flex child (the phone bar's "price / 4 wks" stays together, as the prototype).
        return '<button type="button" class="v2-btn ' + cls + '" data-buy="' + kind + '"' + (has ? '' : ' disabled') + '><span>' + esc(label) + '</span><span>' + cents + '</span></button>';
      };
      var perWeek = function (c) { return fill(T.per_week, { price: price(V.perServe ? V.perServe(c, W) : Math.round(c / W)) }); };
      var html = '<h2 id="v2-e-co-h" class="v2-sr">' + esc(T.co_heading) + '</h2><div class="v2-e-in">';
      if (sub) {
        html += '<span class="v2-label">' + fill(T.co_every, { n: fig(W) }) + '</span>' +
          '<div class="v2-e-row"><span>' + esc(T.subtotal) + '</span><span data-v2-e-subtotal>' + price(once) + '</span></div>' +
          '<div class="v2-e-row v2-muted"><span>' + (t.pct != null ? fill(T.saving, { pct: fig(t.pct + '%') }) : esc(T.saving_plain)) + '</span><span>−' + price(saving) + '</span></div>' +
          '<div class="v2-e-row v2-e-big">' + price(subT) + '<span class="v2-note">' + fill(T.co_every_after, { n: fig(W) }) + '</span></div>';
      } else if (has) {
        // No saving, so a subtotal row would only repeat the total: the total row is the subtotal.
        html += '<div class="v2-e-row v2-e-big">' + price(once).replace('<span class="ace-price"', '<span class="ace-price" data-v2-e-subtotal') + '<span class="v2-note">' + esc(T.sum_once) + '</span></div>';
      }
      html += has ? '<p class="v2-note">' + perWeek(sub ? subT : once) + '</p><p class="v2-e-supplytot">' + supplyLine() + '</p>' : '<p class="v2-note">' + esc(T.empty) + '</p>';
      if (sub) {
        html += buyBtn('sub', 'v2-btn-primary', T.subscribe, price(subT)) + buyBtn('once', 'v2-btn-outline', T.buy_once, price(once)) + outNote +
          '<ul>' + [T.benefit_1, T.benefit_2, T.benefit_3].filter(Boolean).map(function (b) { return '<li>' + esc(b) + '</li>'; }).join('') + '</ul>';
      } else {
        // No real selling plans (or no Build-a-Box id) yet: one plain line under Buy once, naming no saving.
        html += buyBtn('once', 'v2-btn-primary', T.buy_once, price(once)) + outNote + '<p class="v2-note v2-e-subsoon">' + esc(T.subscribe_soon) + '</p>';
      }
      el('checkout').innerHTML = html + '</div>';
      var mb = el('mb-box');
      mb.innerHTML = '<p class="v2-note v2-e-mbnote">' + (has ? fill(T.mb_note, { price: perWeek(sub ? subT : once).replace(/\.$/, ''), supply: supplyLine() }) : esc(T.empty)) + '</p>' +
        '<div class="v2-e-mbrow">' + (sub ? buyBtn('sub', 'v2-btn-primary', T.subscribe, fill(T.mb_price, { price: price(subT), n: fig(W) })) : buyBtn('once', 'v2-btn-primary', T.buy_once, price(once))) + '</div>';
    }

    /* Flow */
    var reasonFor = function (n) { return n === 1 && !S.sport ? T.reason_sport : n === 2 && !S.timing.length ? T.reason_timing : ''; };
    function renderStepper() {
      $$('[data-v2-e-stepper] li', root).forEach(function (li, i) {
        var k = i + 1, b = $('button', li);
        li.className = k < S.step ? 'is-done' : k === S.step ? 'is-cur' : '';
        if (k < S.step) { b.removeAttribute('tabindex'); b.removeAttribute('aria-disabled'); } else { b.tabIndex = -1; b.setAttribute('aria-disabled', 'true'); }
        if (k === S.step) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
      });
    }
    function updateNext() {
      var r = reasonFor(S.step);
      $$('[data-next]', root).forEach(function (b) { b.disabled = !!r; $('span', b).textContent = S.step === 2 ? T.see_box : T.continue_label; });
      $$('[data-reason]', root).forEach(function (x) { x.textContent = S.step < 3 ? r : ''; x.hidden = !r || S.step === 3; });
    }
    function go(n, focus) {
      S.step = n;
      root.setAttribute('data-step', String(n));
      $$('.v2-e-step', root).forEach(function (s) { s.hidden = +s.getAttribute('data-step') !== n; });
      el('top').classList.toggle('is-compact', n > 1);
      if (n === 2) renderSessions();
      if (n === 3) { if (!S.edited) freshBox(); renderBox(); }
      var box = n === 3;
      el('summary').hidden = box;
      el('checkout').hidden = !box;
      el('nav').hidden = box;
      el('mb-steps').hidden = box;
      el('mb-box').hidden = !box;
      $$('[data-back]', root).forEach(function (b) { b.hidden = n === 1; });
      updateNext(); renderStepper(); renderSummary();
      if (focus) { $('#v2-e-s' + n + '-h', root).focus({ preventScroll: true }); scrollTo({ top: 0, behavior: 'smooth' }); }
    }
    var setTiming = function (v, on) { S.timing = S.timing.filter(function (x) { return x !== v; }); if (on) S.timing.push(v); };

    var form = el('form');
    form.addEventListener('submit', function (e) { e.preventDefault(); });
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'sport') { var sp = sports[t.value]; S.sport = t.value; S.type = sp.type; S.length = sp.length; S.perWeek = sp.perWeek; S.edited = false; }
      if (t.name === 'type') { S.type = t.value; S.edited = false; renderTimings(); }
      if (t.name === 'week') { S.perWeek = +t.value; S.edited = false; renderTimings(); }
      if (t.name === 'timing') { setTiming(t.value, t.checked); S.edited = false; }
      if (t.hasAttribute('data-swap') && t.value) {
        var l = S.lines[+t.getAttribute('data-swap')], p = P[t.value];
        l.handle = p.handle; if (l.weekly) l.qty = E.packsFor(l.weekly, p.pack, W);
        S.edited = true; renderBox(); toast(fill(T.swapped, { product: esc(p.name) }));
      }
      if (t === el('add') && t.value) {
        var a = P[t.value];
        S.lines.push({ handle: a.handle, phase: a.phase || 'during', per: 1, weekly: S.perWeek, qty: E.packsFor(S.perWeek, a.pack, W), rec: false });
        S.edited = true; renderBox(); el('add').focus();
      }
      updateNext(); if (S.step < 3) renderSummary();
    });
    // Enter on a chosen option moves on, for keyboard users.
    form.addEventListener('keydown', function (e) {
      // The length spinbutton: arrows step it, Page Up / Down by an hour, Home / End to the ends (Brief 20).
      if (e.target.matches('[data-v2-e-len]')) {
        var k = { ArrowUp: L.step, ArrowRight: L.step, ArrowDown: -L.step, ArrowLeft: -L.step, PageUp: 1, PageDown: -1 }[e.key];
        if (k) { e.preventDefault(); setLen(S.length + k); return; }
        if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); setLen(e.key === 'Home' ? L.min : L.max); return; }
      }
      if (e.key !== 'Enter' || !e.target.matches('input[name=sport],input[name=type],input[name=week],input[name=timing],[data-v2-e-len]')) return;
      e.preventDefault();
      if (!reasonFor(S.step) && S.step < 3) go(S.step + 1, true);
    });
    root.addEventListener('click', function (e) {
      var c = function (s) { return e.target.closest(s); };
      var nx = c('[data-next]'); if (nx && !nx.disabled) return go(S.step + 1, true);
      var ln = c('[data-len]'); if (ln && !ln.disabled) return setLen(S.length + +ln.getAttribute('data-len') * L.step);
      var bk = c('[data-back]'); if (bk && S.step > 1) return go(S.step - 1, true);
      var st = c('[data-go]'); if (st && +st.getAttribute('data-go') < S.step) return go(+st.getAttribute('data-go'), true);
      var q = c('[data-q]');
      if (q && !q.disabled) {
        var qi = q.getAttribute('data-q'), qd = q.getAttribute('data-d'), ql = S.lines[+qi];
        ql.qty = Math.max(1, ql.qty + +qd); S.edited = true; renderBox();
        var again = $('[data-q="' + qi + '"][data-d="' + qd + '"]', root);
        (again && !again.disabled ? again : $('[data-q="' + qi + '"][data-d="1"]', root)).focus();
        return;
      }
      var rm = c('[data-rm]');
      if (rm) { var gone = P[S.lines[+rm.getAttribute('data-rm')].handle]; S.lines.splice(+rm.getAttribute('data-rm'), 1); S.edited = true; renderBox(); toast(fill(T.removed, { product: esc(gone.name) })); el('add').focus(); return; }
      var sq = c('[data-setq]');
      if (sq) { var pr = sq.getAttribute('data-setq').split(':').map(Number); S.lines[pr[0]].qty = pr[1]; S.edited = true; renderBox(); return; }
      if (c('[data-v2-e-reset]')) { freshBox(); renderBox(); toast(esc(T.reset_toast)); return; }
      var nb = c('[data-v2-notify]');
      // Notify me goes to the footer sign-up (ace-v2.js); the row says it's done.
      if (nb) { S.notify[nb.getAttribute('data-v2-notify')] = true; nb.setAttribute('aria-pressed', 'true'); nb.textContent = T.notified; }
      var buy = c('[data-buy]');
      if (buy && !buy.disabled) {
        var sub = buy.getAttribute('data-buy') === 'sub' && subOk();
        var items = E.cartLines(S.lines, P, { mode: sub ? 'sub' : 'once', bbId: bbId, bbName: cfg.bbName || '' });
        if (!items.length || !V.addItems) return;
        V.addItems(items, sub ? T.added_sub.replace('[n]', W) : T.added_once, cfg.addError, true);
      }
    });
    var toast = function (html) { if (V.toast) V.toast(html.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')); };

    // Deep links: ?sport=golf starts at Your sessions with golf chosen; ?add=<handle> puts that product in the box.
    var spq = qs.get('sport');
    var radio = spq && sports[spq] && $('input[name="sport"][value="' + spq + '"]', root);
    if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); go(2); } else go(1);
    root.classList.add('is-ready');
  }

  function start() { $$('[data-v2-ems]').forEach(init); }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
})();
